import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { accountAPI } from '@/api/client'
import type { AccountResponseDto } from '@/types/account'

/**
 * React Query hook for fetching and caching user account data.
 *
 * @returns {UseQueryResult<AccountResponseDto>} Query result with account data, loading, and error states
 *
 * @example
 * ```tsx
 * const { data: account, isLoading, isError, error, refetch } = useAccount();
 *
 * if (isLoading) return <LoadingState />;
 * if (isError) return <ErrorState error={error} onRetry={refetch} />;
 * if (!account) return null;
 *
 * return <div>{account.firstName} {account.lastName}</div>;
 * ```
 */
export function useAccount(): UseQueryResult<AccountResponseDto, Error> {
  return useQuery<AccountResponseDto, Error>({
    queryKey: ['account'],
    queryFn: accountAPI.getAccount,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3, // Retry failed requests up to 3 times
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  })
}
